import React from "react";
import { Box, CircularProgress } from "@mui/joy";
import {
  PlanningControls,
  PlanningCalendarGrid,
  type DayCellAction,
} from "components/common/PlanningCalendar";
import { useExcursionsPlanningState } from "./hooks/useExcursionsPlanningState";
import type { Excursion, ExcursionPlanningCardContext } from "api/excursions/Excursion.typed";
import { ExcursionStatusCancelled } from "api/excursions/Excursion.typed";
import ExcursionModal from "../../shared/excursionModal/display/ExcursionModal";
import ExcursionSmsModal, {
  buildExcursionSmsRecipients,
  collectExcursionSmsParticipantIds,
  smsCustomerColors,
  type ExcursionSmsPreviewContext,
  type ExcursionSmsSendGroup,
  type SMSRecipient,
} from "../../shared/excursionModal/display/ExcursionSmsModal";
import ExcursionWeekAgendaDay from "./display/ExcursionWeekAgendaDay";
import ExcursionSlotContent from "./display/ExcursionSlotContent";
import ExcursionMonthDayContent from "./display/ExcursionMonthDayContent";
import ExcursionPlanningGroupsDrawer from "./display/ExcursionPlanningGroupsDrawer";
import ExcursionPlanningDayFillLine from "./display/ExcursionPlanningDayFillLine";
import { computePlanningDayFillRate } from "./display/excursionCardUtils";
import { useAuth } from "contexts/api";
import { useAlert } from "contexts/app/notificationToast";
import { userDisplayName } from "utils/userHelper";
import { toLocalDateKey } from "utils/dateTime";
import type { ExcursionModalUser } from "../../shared/excursionModal/excursionModalTypes";

function uniquePositiveIds(ids: number[]): number[] {
  const seen = new Set<number>();
  const out: number[] = [];
  for (const id of ids) {
    if (id < 1 || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }

  return out;
}

function activeExcursionsForDay(list: Excursion[]): Excursion[] {
  return list.filter((ex) => ex.status !== ExcursionStatusCancelled);
}

function excursionSmsGroupLabel(excursion: Excursion): string {
  const name = excursion.name?.trim() || `Sortie #${excursion.id}`;
  const start = excursion.start_time?.trim() || "";
  const end = excursion.end_time?.trim() || "";
  if (start && end) return `${name} · ${start}–${end}`;
  if (start) return `${name} · ${start}`;

  return name;
}
export type ExcursionsPageProps = {
  showTimeSlots?: boolean;
  hoursStart?: number;
  hoursEnd?: number;
  businessHoursStart?: number;
  businessHoursEnd?: number;
};

export default function ExcursionsPage({
  showTimeSlots = true,
  hoursStart = 0,
  hoursEnd = 24,
  businessHoursStart = 7,
  businessHoursEnd = 18,
}: ExcursionsPageProps = {}) {
  const state = useExcursionsPlanningState({ showTimeSlots, hoursStart, hoursEnd });
  const { getUsers } = useAuth();
  const { showAlert } = useAlert();

  const {
    loading,
    viewMode,
    setViewMode,
    setCurrentDate,
    days,
    monthGridOffset,
    formatPeriodTitle,
    navigatePeriod,
    getExcursionsForDay,
    getExcursionsForSlot,
    canEdit,
    canSendSMS,
    canOpenExcursionDrawer,
    modalOpen,
    initialDate,
    initialStartTime,
    initialEndTime,
    editingExcursion,
    handleDayClick,
    openModalForSlot,
    handleExcursionClick,
    handleCloseModal,
    handleSave,
    handleDelete,
    persistExcursionAndKeepModalOpen,
    hoursStart: stateHoursStart,
    hoursEnd: stateHoursEnd,
    showTimeSlots: stateShowTimeSlots,
    silentReload,
    getGroupLabels,
    getCustomerInProgressTrainingsTooltip,
    rawCustomers,
    excursions,
    groupsDrawerExcursionId,
    openPlanningGroupsDrawer,
    closePlanningGroupsDrawer,
    handleDragEnterCard,
    handleDragLeaveCard,
    planningChipDragTooltipTargetId,
    dismissPlanningForcedPopoverTarget,
    makeGroupChipDropHandlers,
    makeExcursionCardDragHandlers,
    makeExcursionCardDropHandlers,
    handleExcursionDrop,
    handleExcursionResize,
    onExcursionDragOver,
    draggingExcursion,
    dragProjection,
    handleExcursionDropOnDay,
    clientChipDragInProgress,
    clientChipDragSourceExcursionId,
    onClientChipDragStart,
    onClientChipDragEnd,
    excursionTemplatesList,
    bookingStatusMap,
    chipDropPending,
  } = state;

  const [smsOpen, setSmsOpen] = React.useState(false);
  const [smsSendGroups, setSmsSendGroups] = React.useState<ExcursionSmsSendGroup[]>([]);
  const [smsRecipients, setSmsRecipients] = React.useState<SMSRecipient[]>([]);
  const [smsPreviewContext, setSmsPreviewContext] = React.useState<
    ExcursionSmsPreviewContext | undefined
  >(undefined);
  const [smsLoading, setSmsLoading] = React.useState(false);

  const loadSmsUsers = React.useCallback(async (): Promise<ExcursionModalUser[]> => {
    const userList = (await getUsers()) ?? [];

    return userList.map((u) => ({
      id: u.user.id,
      name: userDisplayName(u.user) || `Utilisateur #${u.user.id}`,
      first_name: u.user.first_name?.trim() || undefined,
      last_name: u.user.last_name?.trim() || undefined,
      qualification: u.user.last_qualification?.trim() || undefined,
      phone_number: u.user.phone_number?.trim() || undefined,
    }));
  }, [getUsers]);

  const closeSmsModal = React.useCallback(() => {
    setSmsOpen(false);
    setSmsSendGroups([]);
    setSmsRecipients([]);
    setSmsPreviewContext(undefined);
  }, []);

  const handleOpenSms = React.useCallback(
    async (excursion: Excursion) => {
      if (!canSendSMS || smsLoading) return;
      setSmsLoading(true);
      try {
        const { userIds, customerIds } = collectExcursionSmsParticipantIds({
          groups: excursion.groups,
          params: (excursion.params ?? null) as Record<string, unknown> | null,
        });
        const groupUserIds = uniquePositiveIds(userIds);
        const groupCustomerIds = uniquePositiveIds(customerIds);
        const users = await loadSmsUsers();
        const recipients = buildExcursionSmsRecipients({
          userIds: groupUserIds,
          customerIds: groupCustomerIds,
          users,
          customers: rawCustomers,
        });
        if (recipients.length === 0) {
          showAlert("Aucun participant avec numéro de téléphone", "warning");

          return;
        }
        setSmsSendGroups([
          {
            excursionId: excursion.id,
            label: excursionSmsGroupLabel(excursion),
            userIds: groupUserIds,
            customerIds: groupCustomerIds,
            customerColors: smsCustomerColors(
              (excursion.groups ?? []).map((g) => ({
                color: g.color,
                customers: g.excursion_customers,
              })),
            ),
          },
        ]);
        setSmsRecipients(recipients);
        setSmsPreviewContext({
          excursionName: excursion.name,
          date: excursion.date,
          startTime: excursion.start_time ?? undefined,
          endTime: excursion.end_time ?? undefined,
        });
        setSmsOpen(true);
      } finally {
        setSmsLoading(false);
      }
    },
    [canSendSMS, loadSmsUsers, rawCustomers, showAlert, smsLoading],
  );

  const handleOpenSmsForDay = React.useCallback(
    async (day: Date) => {
      if (!canSendSMS || smsLoading) return;
      setSmsLoading(true);
      try {
        const dayExcursions = activeExcursionsForDay(getExcursionsForDay(day));
        if (dayExcursions.length === 0) {
          showAlert("Aucune sortie ce jour", "warning");

          return;
        }

        const sendGroups: ExcursionSmsSendGroup[] = [];
        const allUserIds: number[] = [];
        const allCustomerIds: number[] = [];

        for (const excursion of dayExcursions) {
          const { userIds, customerIds } = collectExcursionSmsParticipantIds({
            groups: excursion.groups,
            params: (excursion.params ?? null) as Record<string, unknown> | null,
          });
          const groupUserIds = uniquePositiveIds(userIds);
          const groupCustomerIds = uniquePositiveIds(customerIds);
          if (groupUserIds.length === 0 && groupCustomerIds.length === 0) continue;

          sendGroups.push({
            excursionId: excursion.id,
            label: excursionSmsGroupLabel(excursion),
            userIds: groupUserIds,
            customerIds: groupCustomerIds,
            customerColors: smsCustomerColors(
              (excursion.groups ?? []).map((g) => ({
                color: g.color,
                customers: g.excursion_customers,
              })),
            ),
          });
          allUserIds.push(...groupUserIds);
          allCustomerIds.push(...groupCustomerIds);
        }

        if (sendGroups.length === 0) {
          showAlert("Aucun participant ce jour", "warning");

          return;
        }

        const users = await loadSmsUsers();
        const recipients = buildExcursionSmsRecipients({
          userIds: uniquePositiveIds(allUserIds),
          customerIds: uniquePositiveIds(allCustomerIds),
          users,
          customers: rawCustomers,
        });
        if (recipients.length === 0) {
          showAlert("Aucun participant avec numéro de téléphone", "warning");

          return;
        }

        const first = dayExcursions[0];
        setSmsSendGroups(sendGroups);
        setSmsRecipients(recipients);
        setSmsPreviewContext({
          date: toLocalDateKey(day),
          ...(dayExcursions.length === 1
            ? {
                excursionName: first?.name,
                startTime: first?.start_time ?? undefined,
                endTime: first?.end_time ?? undefined,
              }
            : {}),
        });
        setSmsOpen(true);
      } finally {
        setSmsLoading(false);
      }
    },
    [canSendSMS, getExcursionsForDay, loadSmsUsers, rawCustomers, showAlert, smsLoading],
  );

  const dayCellActions = React.useMemo<DayCellAction[] | undefined>(() => {
    if (!canSendSMS) return undefined;

    return [
      {
        key: "send_sms",
        label: "Envoyer SMS",
        emoji: "💬",
        onAction: (day) => void handleOpenSmsForDay(day),
      },
    ];
  }, [canSendSMS, handleOpenSmsForDay]);

  const planningCardContext: ExcursionPlanningCardContext = React.useMemo(
    () => ({
      canEdit,
      canOpenExcursionDrawer,
      getGroupLabels,
      getCustomerInProgressTrainingsTooltip,
      makeGroupChipDropHandlers,
      makeExcursionCardDragHandlers,
      makeExcursionCardDropHandlers,
      openPlanningGroupsDrawer,
      onOpenExcursionFullModal: handleExcursionClick,
      tooltipForceClose:
        modalOpen ||
        groupsDrawerExcursionId == null ||
        (!clientChipDragInProgress && planningChipDragTooltipTargetId == null),
      clientChipDragInProgress,
      clientChipDragSourceExcursionId,
      onClientChipDragStart,
      onClientChipDragEnd,
      onDragEnterCard: handleDragEnterCard,
      onDragLeaveCard: handleDragLeaveCard,
      planningChipDragTooltipTargetId,
      dismissPlanningForcedPopoverTarget,
      excursionTemplatesList,
      bookingStatusMap,
      chipDropPending,
    }),
    [
      canEdit,
      canOpenExcursionDrawer,
      getGroupLabels,
      getCustomerInProgressTrainingsTooltip,
      makeGroupChipDropHandlers,
      makeExcursionCardDragHandlers,
      makeExcursionCardDropHandlers,
      openPlanningGroupsDrawer,
      handleExcursionClick,
      modalOpen,
      groupsDrawerExcursionId,
      clientChipDragInProgress,
      clientChipDragSourceExcursionId,
      onClientChipDragStart,
      onClientChipDragEnd,
      handleDragEnterCard,
      handleDragLeaveCard,
      planningChipDragTooltipTargetId,
      dismissPlanningForcedPopoverTarget,
      excursionTemplatesList,
      bookingStatusMap,
      chipDropPending,
    ],
  );

  const planningDrawerExcursion = React.useMemo(
    () =>
      groupsDrawerExcursionId == null
        ? null
        : (excursions.find((ex) => ex.id === groupsDrawerExcursionId) ?? null),
    [excursions, groupsDrawerExcursionId],
  );

  const drawerOpen = Boolean(planningDrawerExcursion);

  const renderWeekAgendaDay = React.useCallback(
    (
      day: Date,
      _columnHeightPx: number,
      pixelsPerHour: number,
      _isToday: boolean,
      _isWeekend: boolean,
    ) => (
      <ExcursionWeekAgendaDay
        day={day}
        pixelsPerHour={pixelsPerHour}
        hoursStart={stateHoursStart}
        hoursEnd={stateHoursEnd}
        dayExcursions={getExcursionsForDay(day)}
        openModalForSlot={openModalForSlot}
        onExcursionDrop={handleExcursionDrop}
        onExcursionResize={handleExcursionResize}
        onExcursionDragOver={onExcursionDragOver}
        draggingExcursion={draggingExcursion}
        dragProjection={dragProjection}
        {...planningCardContext}
      />
    ),
    [
      stateHoursStart,
      stateHoursEnd,
      getExcursionsForDay,
      openModalForSlot,
      handleExcursionDrop,
      handleExcursionResize,
      onExcursionDragOver,
      draggingExcursion,
      dragProjection,
      planningCardContext,
    ],
  );

  const renderTimeSlotContent = React.useCallback(
    (day: Date, hour: number, _isToday: boolean, _isWeekend: boolean) => {
      const slotExcursions = getExcursionsForSlot(day, hour);
      return (
        <ExcursionSlotContent
          day={day}
          hour={hour}
          slotExcursions={slotExcursions}
          openModalForSlot={openModalForSlot}
          onExcursionDrop={handleExcursionDrop}
          {...planningCardContext}
        />
      );
    },
    [getExcursionsForSlot, openModalForSlot, handleExcursionDrop, planningCardContext],
  );

  const renderDayContent = React.useCallback(
    (day: Date, isToday: boolean, isWeekend: boolean) => (
      <ExcursionMonthDayContent
        day={day}
        isToday={isToday}
        isWeekend={isWeekend}
        dayExcursions={getExcursionsForDay(day)}
        onDayClick={handleDayClick}
        onExcursionDropOnDay={handleExcursionDropOnDay}
        {...planningCardContext}
      />
    ),
    [getExcursionsForDay, handleDayClick, handleExcursionDropOnDay, planningCardContext],
  );

  const renderDayHeaderFooter = React.useCallback(
    (day: Date, _isToday: boolean, _isWeekend: boolean) => {
      const list = getExcursionsForDay(day);
      const rate = computePlanningDayFillRate(list, excursionTemplatesList);

      if (!rate) {
        return null;
      }

      return (
        <ExcursionPlanningDayFillLine
          totalPeople={rate.totalPeople}
          totalMax={rate.totalMax}
          percent={rate.percent}
        />
      );
    },
    [getExcursionsForDay, excursionTemplatesList],
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2 },
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <PlanningControls
          periodTitle={formatPeriodTitle()}
          viewMode={viewMode}
          onPrev={() => navigatePeriod("prev")}
          onNext={() => navigatePeriod("next")}
          onToday={() => setCurrentDate(new Date())}
          onViewModeChange={setViewMode}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "stretch",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <PlanningCalendarGrid
            fillHeight
            viewMode={viewMode}
            days={days}
            monthGridOffset={monthGridOffset}
            showTimeSlots={stateShowTimeSlots}
            hoursStart={stateHoursStart}
            hoursEnd={stateHoursEnd}
            viewportHoursStart={5}
            viewportHoursEnd={20}
            businessHoursStart={businessHoursStart}
            businessHoursEnd={businessHoursEnd}
            allDaysWorking
            dayCellActions={dayCellActions}
            renderWeekAgendaDay={stateShowTimeSlots ? renderWeekAgendaDay : undefined}
            renderTimeSlotContent={stateShowTimeSlots ? renderTimeSlotContent : undefined}
            renderDayContent={renderDayContent}
            renderDayHeaderFooter={renderDayHeaderFooter}
          />
        </Box>
        <ExcursionPlanningGroupsDrawer
          open={drawerOpen}
          excursion={planningDrawerExcursion}
          onClose={closePlanningGroupsDrawer}
          canEdit={canEdit}
          getGroupLabels={getGroupLabels}
          makeGroupChipDropHandlers={makeGroupChipDropHandlers}
          onClientChipDragStart={onClientChipDragStart}
          onClientChipDragEnd={onClientChipDragEnd}
          onNavigateFullExcursionModal={handleExcursionClick}
          onOpenSms={(ex) => void handleOpenSms(ex)}
          showSmsButton={canSendSMS}
          excursionTemplatesList={excursionTemplatesList}
          bookingStatusMap={
            planningDrawerExcursion ? bookingStatusMap?.[planningDrawerExcursion.id] : undefined
          }
          getCustomerInProgressTrainingsTooltip={getCustomerInProgressTrainingsTooltip}
          chipDropPending={chipDropPending}
        />
      </Box>
      <ExcursionModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        onDelete={editingExcursion ? () => handleDelete(editingExcursion.id) : undefined}
        initialDate={initialDate}
        initialExcursion={editingExcursion ?? undefined}
        initialStartTime={initialStartTime}
        initialEndTime={initialEndTime}
        canEdit={canEdit}
        onValidateCountersApplied={silentReload}
        onPersistAfterCounters={persistExcursionAndKeepModalOpen}
      />
      {smsSendGroups.length > 0 && (
        <ExcursionSmsModal
          open={smsOpen}
          onClose={closeSmsModal}
          sendGroups={smsSendGroups}
          initialRecipients={smsRecipients}
          previewContext={smsPreviewContext}
        />
      )}
    </Box>
  );
}
